import type { ContentSkill, MotorSkill } from "../types/prisma-enums";
import type { GameId } from "../curriculum/types";
import type { Localized } from "../i18n";
import { assetUrl } from "../assets/assetUrl";

/**
 * Which game teaches a content skill, and the plate that game is drawn on.
 *
 * 🔴 THIS IS THE ONE THING A PARENT CANNOT GET FROM A BAR. "Numbers · Level 2 of
 * 3" is a measurement; *"Sky Quest"* is the thing their child talks about at
 * dinner. design-system.md §3 already fixes the pairing — one game per content
 * skill, one colour per skill — so naming the game here adds no fact, it just
 * stops the parent tier being the only surface that knows the number without
 * knowing what it is about.
 *
 * 🔴 A `Record<ContentSkill, …>`, WHICH IS WHY THERE IS NO TEST FOR IT. A sixth
 * content skill added to the Prisma enum fails the **build** rather than a test
 * — `CONTENT_SKILLS` is generated from the schema, and a missing key here is a
 * type error at the point the skill is introduced. That is the same guarantee
 * `skillSections` gets by mapping over the enum, one level earlier.
 *
 * ⚠️ THE ART IS THE GAME'S OWN PLATE, NOT DECORATION, AND THAT IS THE WHOLE
 * DEFENCE FOR IT BEING HERE. design-system.md is explicit that an adult shell's
 * warmth comes *"never from cartoon elements"* — a cartoon dropped onto a
 * parent's report to make it feel friendly is exactly what that forbids. These
 * five images are what the child sees when they pick a game; showing the parent
 * the same picture is showing them their child's week, and it is the one kind of
 * playfulness on this screen that carries information.
 *
 * ⚠️ THE TITLE IS PAINTED INTO EVERY PLATE (see `kid/HomeScreen.tsx`), so the
 * name below the art is not a caption repeating the picture — it is the text
 * alternative for it — the string a screen reader announces, and what a parent
 * gets when the plate does not load.
 */

export interface GameArt {
  /** The  the events table records. */
  id: GameId;
  /** Public path. Every plate is 3:2 and safe to crop at the edges. */
  art: string;
  /** The game's name, as a parent would say it. */
  title: Localized<string>;
  /** The skill's fixed colour token — design-system.md §3, never re-picked here. */
  tint: string;
}

export const GAME_FOR_SKILL: Record<ContentSkill, GameArt> = {
  NUMERACY: {
    id: "sky-quest",
    art: assetUrl("/games/sky-quest/cover.webp"),
    title: { en: "Sky Quest" },
    tint: "var(--color-skill-numeracy)",
  },
  LITERACY: {
    id: "letter-lagoon",
    art: assetUrl("/games/letter-lagoon/cover.webp"),
    title: { en: "Letter Lagoon" },
    tint: "var(--color-skill-literacy)",
  },
  GENERAL_AWARENESS: {
    id: "turos-world",
    art: assetUrl("/games/turos-world/cover.webp"),
    title: { en: "Turo’s World" },
    tint: "var(--color-skill-general-awareness)",
  },
  SOCIAL_AWARENESS: {
    id: "kalqy-world",
    art: assetUrl("/games/kalqy-world/cover.webp"),
    title: { en: "Kalqy World" },
    tint: "var(--color-skill-social-awareness)",
  },
  MATCHING: {
    id: "bridge-patterns",
    art: assetUrl("/games/bridge-patterns/cover.webp"),
    title: { en: "Bridge Patterns" },
    tint: "var(--color-skill-matching)",
  },
};

/**
 * The motor skills' colours, for the same reason and from the same table.
 *
 * ⚠️ NO ART. A motor skill is earned across all five games (§3), so there is no
 * one plate that is honestly its picture — and picking the prettiest would tell
 * a parent their child's balance comes from Letter Lagoon alone.
 */
export const MOTOR_TINT: Record<MotorSkill, string> = {
  COORDINATION: "var(--color-skill-coordination)",
  BALANCE: "var(--color-skill-balance)",
  BODY_AWARENESS: "var(--color-skill-body-awareness)",
};

/**
 * The same five entries, keyed by the id the events table records.
 *
 * 🔴 IT EXISTS BECAUSE E5 WAS PRINTING DATABASE SLUGS AT A HEAD TEACHER.
 * `NcfCoverage.rows` is keyed by `GameId`, and the reports table rendered it
 * raw — "sky-quest", "letter-lagoon", "turos-world" — in the bold first column
 * of a document a school might act on. The product's own name for that game has
 * existed in this file all along; the report simply had no way to reach it.
 *
 * ⚠️ DERIVED, NEVER A SECOND TABLE. Two hand-written maps keyed differently are
 * two maps that disagree the first time a game is renamed.
 */
export const GAME_ART_BY_ID: Record<GameId, GameArt> = Object.fromEntries(
  Object.values(GAME_FOR_SKILL).map((g) => [g.id, g]),
) as Record<GameId, GameArt>;
